// src/components/ReferralPopup.js - VERSION FINALE AVEC LOGS DE VÉRIFICATION

import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios'; 
import './ReferralPopup.css';

// Configuration de l'instance Axios
const api = axios.create({
    baseURL: 'https://api.blackroq.pro/api' // Assurez-vous que l'URL est correcte
});

const ReferralPopup = () => {
    const location = useLocation();
    const navigate = useNavigate();
    
    // État pour stocker les informations valides de l'offre et du code
    const [refId, setRefId] = useState(null);
    const [prefilledPlan, setPrefilledPlan] = useState('');
    const [prefilledAmount, setPrefilledAmount] = useState('');
    const [referrerUsername, setReferrerUsername] = useState(''); // Pour l'affichage "parrainé par X"

    const [showPopup, setShowPopup] = useState(false);
    const [errorMessage, setErrorMessage] = useState(''); 
    const [isLoading, setIsLoading] = useState(true); // Démarrer toujours à 'true' si un code est dans l'URL
    const [isDataLoaded, setIsDataLoaded] = useState(false); // Marqueur de succès de la vérification

    /**
     * @description Fonction asynchrone pour vérifier la validité du code de parrainage.
     * @param {string} code Le code de parrainage extrait de l'URL.
     */
    const checkReferralValidity = useCallback(async (code) => {
        setIsLoading(true);
        // Réinitialisation des messages précédents
        setErrorMessage(''); 
        setIsDataLoaded(false); 
        
        try {
            console.log(`[FRONTEND DEBUG] Tentative de vérification pour le code: ${code}`);

            // Appel à la route backend /api/investment/check-referral
            const response = await api.get(`/investment/check-referral?code=${code}`); 
            
            // 💡 LOG CRITIQUE 1 : Vérifiez la réponse brute du backend
            console.log("✅ [FRONTEND DEBUG] Réponse de vérification reçue:", response.data); 

            // Assurez-vous que les clés correspondent EXACTEMENT à ce que votre backend envoie.
            // Si le backend utilise 'plan_name' au lieu de 'planName', la déstructuration échoue ici!
            const { planName, amountInOriginalCurrency, referrerUsername } = response.data;
            
            // Si les données sont manquantes malgré le 200 (problème de clés)
            if (!planName || !amountInOriginalCurrency) {
                 console.error("❌ [FRONTEND DEBUG] Erreur de déstructuration: Les clés planName ou amountInOriginalCurrency sont manquantes ou incorrectes.");
                 throw new Error("Données de parrainage incomplètes (Vérifiez les noms de clés)."); 
            }

            // 💡 LOG CRITIQUE 2 : Confirmez que les valeurs sont correctement extraites
            console.log(`✅ [FRONTEND DEBUG] Valeurs extraites: Plan=${planName}, Montant=${amountInOriginalCurrency}`);
            
            // Les données sont validées et stockées
            setPrefilledPlan(planName);
            setPrefilledAmount(amountInOriginalCurrency);
            setReferrerUsername(referrerUsername || 'votre parrain'); // Fallback si le nom n'est pas fourni
            
            // MARQUER LE SUCCÈS
            setIsDataLoaded(true); 
            
        } catch (error) {
            // 💡 LOG CRITIQUE 3 : Affiche l'erreur si le backend renvoie un code != 200 OU si une erreur JS est lancée (comme le throw ci-dessus)
            console.error("❌ [FRONTEND DEBUG] ERREUR EN CAPTURE:", error.response?.data || error.message);
            
            // Si le backend renvoie 400/404/500, on prend son message d'erreur
            const msg = error.response?.data?.message || "Ce lien de parrainage est invalide ou n'existe pas.";
            setErrorMessage(msg);
            
            // Nettoyer les plans pré-remplis en cas d'erreur
            setPrefilledPlan('');
            setPrefilledAmount('');
            setIsDataLoaded(false);
            
        } finally {
            // FIN DE LA VÉRIFICATION
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const referralId = params.get('referral'); 
        
        if (referralId && referralId.length > 0) {
            setRefId(referralId);
            setShowPopup(true);
            setIsLoading(true); // Déclencher le chargement
            
            // On vérifie le code dès le chargement pour avoir les données du plan
            checkReferralValidity(referralId); 
            
        } else {
            setShowPopup(false);
            setIsLoading(false); 
        }
        
    }, [location.search, checkReferralValidity]); // checkReferralValidity est dans les dépendances car c'est une dépendance externe

    const handleClose = () => {
        setShowPopup(false);
        // Nettoyer l'URL
        navigate(location.pathname, { replace: true });
    };

    const handleProceed = () => {
        // Si on a une erreur, on clique pour fermer
        if (errorMessage) {
            handleClose();
            return;
        }

        // Vérification finale du succès
        if (!isDataLoaded || !refId || !prefilledPlan || !prefilledAmount) {
            console.error("❌ [FRONTEND DEBUG] Tentative de continuer sans données de succès.");
            setErrorMessage("Impossible de continuer, veuillez vérifier la validité du lien.");
            return;
        }

        // ✅ SUCCÈS : Redirection vers la confirmation
        setShowPopup(false);
        
        // Redirection avec les query params
        navigate(`/subscribe?plan=${encodeURIComponent(prefilledPlan)}&amount=${prefilledAmount}&referral=${refId}`); 
    };

    if (!showPopup) {
        return null;
    }

    // Affiche l'état de chargement
    if (isLoading) {
        return (
            <div className="popup-overlay-referral">
                <div className="popup-content-referral">
                    <h2>Vérification du lien de parrainage...</h2>
                    <p>Veuillez patienter.</p>
                </div>
            </div>
        );
    }

    // Affiche le contenu (erreur ou succès) après le chargement
    return (
        <div className="popup-overlay-referral">
            <div className="popup-content-referral">
                <button className="close-btn-referral" onClick={handleClose}>×</button>
                
                <div className="popup-header-referral">
                    <h2>{errorMessage ? "Attention !" : "Félicitations ! 🎉"}</h2>
                    <p>{errorMessage ? "Une erreur est survenue." : `Vous avez été parrainé par ${referrerUsername}.`}</p>
                </div>
                
                {errorMessage ? (
                    <div className="alert alert-danger p-3 mb-4 rounded">
                        <p className="fw-bold mb-1">Lien de parrainage non valide :</p>
                        <p className="mb-0 small">{errorMessage}</p>
                    </div>
                ) : (
                    // 💡 Bloc Succès (s'affiche seulement si errorMessage est vide après chargement)
                    <div className="popup-body-referral">
                        <p>
                            En tant que nouveau membre, vous êtes éligible pour un **bonus exclusif de bienvenue** en complétant cet investissement.
                        </p>
                        <p className="fw-bold">
                            Vous allez continuer avec le plan : **{decodeURIComponent(prefilledPlan)}** pour **{Number(prefilledAmount).toLocaleString()} FCFA**.
                        </p>
                        <p className="text-muted small mt-2">
                            Confirmez pour vous diriger vers le formulaire et obtenir votre bonus.
                        </p>
                    </div>
                )}
                
                <div className="popup-footer-referral">
                    <button 
                        className={`proceed-btn-referral ${errorMessage ? 'btn-danger' : 'btn-primary'}`} 
                        onClick={errorMessage ? handleClose : handleProceed}
                        disabled={isLoading}
                    >
                        {errorMessage ? "Fermer et Annuler" : "Continuer"}
                    </button>
                    
                    {!errorMessage && (
                        <button 
                            className="cancel-btn-referral" 
                            onClick={handleClose}
                            disabled={isLoading}
                        >
                            Annuler et fermer
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ReferralPopup;